import type { SessionEntity } from '../../domain/entities/SessionEntity.js';
import type { ISessionRepository, SessionCreateInput, SessionExportEntity, SessionExportOptions, SessionListOptions, SessionOverlapCheckOptions, SessionOverlapEntity, SessionSearchOptions, SessionUpdateInput } from '../../domain/repositories/ISessionRepository.js';
import { SessionRemoteDataSource } from '../datasources/SessionRemoteDataSource.js';
import type { SessionDto } from '../dtos/SessionDto.js';

function mapDto(dto: SessionDto): SessionEntity {
  return {
    id: dto.id,
    orgId: dto.org_id,
    projectId: dto.project_id,
    createdBy: dto.created_by,
    achieverEmail: dto.achiever_profile?.email ?? null,
    achieverId: dto.achiever_id ?? null,
    startedAt: dto.started_at ?? null,
    endedAt: dto.ended_at ?? null,
    scheduledStartAt: dto.scheduled_start_at ?? null,
    scheduledEndAt: dto.scheduled_end_at ?? null,
    locationId: dto.location_id ?? null,
    locationName: dto.location?.name ?? null,
    startedLat: dto.started_lat ?? null,
    startedLng: dto.started_lng ?? null,
    endedLat: dto.ended_lat ?? null,
    endedLng: dto.ended_lng ?? null,
    minimumGeoFenceMeters: dto.minimum_geo_fence_meters ?? null,
    minimumDurationMinutes: dto.minimum_duration_minutes ?? null,
    requiredPhotoCount: dto.required_photo_count ?? null,
    startPhotoUrls: dto.start_photo_urls ?? [],
    endPhotoUrls: dto.end_photo_urls ?? [],
    notes: dto.notes ?? null,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  };
}

export class SessionRepository implements ISessionRepository {
  constructor(private readonly ds: SessionRemoteDataSource) { }

  async list(options: SessionListOptions): Promise<{ data: SessionEntity[]; total: number }> {
    const { data, total } = await this.ds.list(options);
    return { data: data.map(mapDto), total };
  }

  async search(options: SessionSearchOptions): Promise<{ data: SessionEntity[]; total: number }> {
    const { data, total } = await this.ds.search(options);
    return { data: data.map(mapDto), total };
  }

  async getById(id: string): Promise<SessionEntity | null> {
    const dto = await this.ds.getById(id);
    return dto ? mapDto(dto) : null;
  }

  async create(input: SessionCreateInput): Promise<SessionEntity> {
    const dto = await this.ds.create({
      org_id: input.orgId,
      project_id: input.projectId,
      achiever_id: input.achieverId ?? null,
      location_id: input.locationId ?? null,
      scheduled_start_at: input.scheduledStartAt ?? null,
      scheduled_end_at: input.scheduledEndAt ?? null,
      minimum_geo_fence_meters: input.minimumGeoFenceMeters ?? null,
      minimum_duration_minutes: input.minimumDurationMinutes ?? null,
      required_photo_count: input.requiredPhotoCount ?? null,
      notes: input.notes ?? null,
      start_photo_urls: input.startPhotoUrls ?? [],
      end_photo_urls: input.endPhotoUrls ?? [],
    });
    if (!dto) throw new Error('Session creation failed');
    return mapDto(dto);
  }

  async update(id: string, input: SessionUpdateInput): Promise<SessionEntity | null> {
    const payload: Partial<SessionDto> = {};
    if (input.achieverId !== undefined) payload.achiever_id = input.achieverId;
    if (input.locationId !== undefined) payload.location_id = input.locationId;
    if (input.scheduledStartAt !== undefined) payload.scheduled_start_at = input.scheduledStartAt;
    if (input.scheduledEndAt !== undefined) payload.scheduled_end_at = input.scheduledEndAt;
    if (input.minimumGeoFenceMeters !== undefined) payload.minimum_geo_fence_meters = input.minimumGeoFenceMeters;
    if (input.minimumDurationMinutes !== undefined) payload.minimum_duration_minutes = input.minimumDurationMinutes;
    if (input.requiredPhotoCount !== undefined) payload.required_photo_count = input.requiredPhotoCount;
    if (input.startPhotoUrls !== undefined) payload.start_photo_urls = input.startPhotoUrls;
    if (input.endPhotoUrls !== undefined) payload.end_photo_urls = input.endPhotoUrls;
    if (input.startedAt !== undefined) payload.started_at = input.startedAt;
    if (input.endedAt !== undefined) payload.ended_at = input.endedAt;
    if (input.startedLat !== undefined) payload.started_lat = input.startedLat;
    if (input.startedLng !== undefined) payload.started_lng = input.startedLng;
    if (input.endedLat !== undefined) payload.ended_lat = input.endedLat;
    if (input.endedLng !== undefined) payload.ended_lng = input.endedLng;
    if (input.notes !== undefined) payload.notes = input.notes;
    const dto = await this.ds.update(id, payload);
    return dto ? mapDto(dto) : null;
  }

  async delete(id: string): Promise<void> {
    await this.ds.delete(id);
  }

  async checkAchieverOverlap(options: SessionOverlapCheckOptions): Promise<SessionOverlapEntity[]> {
    const rows = await this.ds.checkAchieverOverlap({
      achieverId: options.achieverId,
      scheduledStartAt: options.scheduledStartAt,
      scheduledEndAt: options.scheduledEndAt,
      excludeSessionId: options.excludeSessionId,
    });
    return rows.map((r) => ({
      id: r.id,
      projectId: r.project_id,
      locationId: r.location_id,
      scheduledStartAt: r.scheduled_start_at,
      scheduledEndAt: r.scheduled_end_at,
    }));
  }

  async exportByProject(options: SessionExportOptions): Promise<SessionExportEntity[]> {
    const rows = await this.ds.exportByProject(options);
    return rows.map((dto) => {
      const meta = (dto.project_location?.metadata ?? {}) as Record<string, unknown>;
      const str = (v: unknown): string | null =>
        v == null || v === '' ? null : String(v);
      return {
        ...mapDto(dto),
        locationName: dto.location?.name ?? null,
        locationCode: dto.location?.code ?? null,
        addressLine: dto.location?.address_line ?? null,
        fullAddressCache: dto.location?.full_address_cache ?? null,
        categories: dto.location?.categories ?? [],
        provinceCode: dto.location?.province_code ?? null,
        provinceName: dto.location?.province_name ?? null,
        wardCode: dto.location?.ward_code ?? null,
        wardName: dto.location?.ward_name ?? null,
        geoFenceRadiusM: dto.project_location?.geo_fence_radius_m ?? null,
        projectLocationCode: dto.project_location?.custom_code ?? null,
        supervisor: str(meta.supervisor),
        supervisorPhone: str(meta.supervisorPhone),
        archiverPhone: str(meta.archiverPhone),
        achieverEmail: str(dto.achiever_profile?.email),
        achieverName: str(dto.achiever_profile?.full_name),
        achieverPhone: str(dto.achiever_profile?.phone),
      };
    });
  }
}
